import React, { useContext, memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import { Prompt } from 'react-router-dom';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Button, CollapseCard } from '@totalsoft/rocket-ui';
import LeasingExpensesRowComponent from './LeasingExpensesRowComponent';
import { RightsContext } from 'providers/RightsContext';
import LeasingExpensesSummaryComponent from './LeasingExpensesSummaryComponent';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { GridActionsWrapper } from 'components/common/CommonStyles';

export const LeasingExpensesComponent = ({
  t,
  leasingExpenses,
  currencies,
  leasingExpenseTypes,
  onPropertyChange,
  onAdd,
  onDelete,
  offerCurrency,
  onSave,
  saving,
  deleted
}) => {
  const { isImage, readOnlyFull, addDeleteFees, financialFeesWrite, financialFeesRead } = useContext(RightsContext);

  return (
    <>
      <Prompt when={leasingExpenses && leasingExpenses.some(x => x.dirtyInfo.isDirty)} message={t('LeavingWithoutSaving')} />
      {financialFeesRead && (
        <CollapseCard
          id={'leasingExpensesCard'}
          icon={EuroSymbol}
          title={t('OfferEdit.FixedCommissions.Title')}
          subheader={<LeasingExpensesSummaryComponent items={leasingExpenses} offerCurrency={offerCurrency} />}
          hideSubheaderOnExpand
          toggleOnHeaderClick
          expanded={false}
        >
          <Grid container>
            <Grid item xs={12}>
              <CardNoResultsWrapper content={leasingExpenses}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>{t('OfferEdit.FixedCommissions.Title')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('OfferEdit.FixedCommissions.ExpenseValue') + `(${offerCurrency})`}</strong>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {leasingExpenses.map((item, idx) => {
                        return (
                          <LeasingExpensesRowComponent
                            item={item.document}
                            key={idx}
                            idx={idx}
                            currencies={currencies}
                            leasingExpenseTypes={leasingExpenseTypes}
                            onPropertyChange={onPropertyChange}
                            onDelete={onDelete}
                            onSave={onSave}
                            validationInfo={item.validationInfo}
                            disabled={readOnlyFull}
                            addDeleteFees={addDeleteFees}
                            financialFeesWrite={financialFeesWrite}
                          />
                        );
                      })}
                    </>
                  </TableBody>
                </Table>
              </CardNoResultsWrapper>
              {!isImage && (
                <GridActionsWrapper mt={2}>
                  <Button
                    id={'leasingExpensesCard_addBtn'}
                    color='primary'
                    variant='contained'
                    disabled={readOnlyFull || !financialFeesWrite || !addDeleteFees}
                    onClick={onAdd}
                  >
                    {t('Add')}
                  </Button>
                  <Button
                    id={'leasingExpensesCard_saveBtn'}
                    color='primary'
                    variant='contained'
                    onClick={onSave}
                    disabled={
                      saving || !financialFeesWrite || (!deleted && !(leasingExpenses && leasingExpenses.some(x => x.dirtyInfo.isDirty)))
                    }
                  >
                    {saving ? t('Saving') : t('Save')}
                  </Button>
                </GridActionsWrapper>
              )}
            </Grid>
          </Grid>
        </CollapseCard>
      )}
    </>
  );
};
LeasingExpensesComponent.propTypes = {
  t: PropTypes.func.isRequired,
  leasingExpenses: PropTypes.array.isRequired,
  currencies: PropTypes.array.isRequired,
  leasingExpenseTypes: PropTypes.array.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  deleted: PropTypes.bool.isRequired,
  offerCurrency: PropTypes.string
};
export default compose(withTranslation())(memo(LeasingExpensesComponent));
